import { JsonObject } from '@bufbuild/protobuf';
import { PhoenixTrpcClient } from 'apps/phoenix/src/app/utils';
import { isEqual } from 'lodash';
import {
  Observable,
  Subject,
  forkJoin,
  from,
  lastValueFrom,
  takeUntil,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { AutocompleteOption } from '../../interfaces';
import { UiHelpersService } from '../../services';

@Component({
  selector: 'phoenix-sources-autocomplete',
  templateUrl: './sources-autocomplete.component.html',
  standalone: false,
})
export class SourcesAutocompleteComponent
  implements OnDestroy, OnChanges, OnInit
{
  phoenixTrpc = inject(PhoenixTrpcClient);
  uiHelper = inject(UiHelpersService);
  destroy$ = new Subject<void>();
  private controlDestroy$ = new Subject<void>();

  @Input() title = '';
  /** The field's own label; `title` is the heading above it. */
  @Input() label = 'Source';
  @Input() showChips = false;
  @Input() showIndex = false;
  @Input() control = new FormControl('');
  @Input() sourcesChipsControl = new FormControl<any[]>([]);
  @Input() notRemovable = false;
  @Input() refresh = new Observable<void>();
  @Input() useStringArray = false;
  @Input() sourceOnly = false;
  @Input() attrToSave: string = 'id';
  @Output() sourceSelected = new EventEmitter<string | AutocompleteOption>();

  notRemovableSource: JsonObject[] = [];
  decoratedSourcesControl = new FormControl<JsonObject[]>([]);

  get displayChipsControl(): FormControl {
    return this.useStringArray
      ? this.decoratedSourcesControl
      : this.sourcesChipsControl;
  }

  loadingSources = false;
  isRefreshing = false;
  source!: any;
  sources!: any[];
  disabled = false;

  ngOnInit(): void {
    this.refresh.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isRefreshing) return;

      if (this.notRemovable) {
        this.notRemovableSource = this.displayChipsControl.value ?? [];
      }

      this.updateSourceChipsControl();
    });
  }

  private syncSourceArrays(): void {
    this.decoratedSourcesControl.setValue(
      this.sourcesChipsControl.value?.map(id => ({
        id,
        descriptor_name: 'source.Source',
      })) ?? [],
      {
        emitEvent: false,
      }
    );
  }

  updateSourceChipsControl(): void {
    if (!this.displayChipsControl.value?.length) return;
    if (this.isRefreshing) return;

    const currentValues = [...(this.displayChipsControl.value || [])];

    // Create an array of source lookup observables
    const sourceObservables = currentValues.map(value =>
      from(this.getSource(value.id, false))
    );

    // Use forkJoin to wait for all source lookups to complete before updating the control
    if (sourceObservables.length) {
      this.isRefreshing = true;
      forkJoin(sourceObservables)
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe({
          next: (sourceDataArray: JsonObject[]) => {
            const updatedValues = currentValues.map((value, index) => ({
              ...value,
              subSourceName: sourceDataArray[index]?.subSourceName,
            }));

            this.displayChipsControl.setValue(updatedValues, {
              emitEvent: false,
            });
            if (this.notRemovable) {
              this.notRemovableSource = updatedValues ?? [];
            }
            this.isRefreshing = false;
          },
          error: () => {
            this.isRefreshing = false;
          },
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['sourcesChipsControl']) {
      this.controlDestroy$.next();
      this.isRefreshing = false;

      this.disabled =
        this.sourcesChipsControl.disabled || this.control.disabled;

      if (this.control.value) {
        this.getSource(this.control.value);
      }

      this.sourcesChipsControl.statusChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(status => {
          if (status === 'DISABLED') {
            this.disabled = true;
          } else {
            this.disabled = false;
          }
        });

      if (this.useStringArray) {
        this.syncSourceArrays();

        this.sourcesChipsControl.valueChanges
          .pipe(takeUntil(this.controlDestroy$))
          .subscribe(() => {
            const stringIds = (this.decoratedSourcesControl.value ?? []).map(
              s => s.id
            );
            const currentStrings = this.sourcesChipsControl.value ?? [];
            if (!isEqual(stringIds, currentStrings)) {
              this.syncSourceArrays();
            }
          });

        this.decoratedSourcesControl.valueChanges
          .pipe(takeUntil(this.controlDestroy$))
          .subscribe(decoratedValues => {
            const stringIds = (decoratedValues ?? []).map(s => s.id);
            const currentStrings = this.sourcesChipsControl.value ?? [];
            if (!isEqual(stringIds, currentStrings)) {
              this.sourcesChipsControl.setValue(stringIds, {
                emitEvent: false,
              });
              this.sourcesChipsControl.markAsDirty();
            }
          });
      }
      if (this.notRemovable) {
        this.notRemovableSource = this.displayChipsControl.value ?? [];
      }

      this.updateSourceChipsControl();
    }
  }

  sourceAutocomplete = (val: string) => {
    return from(
      this.phoenixTrpc.sources.autocomplete.query({
        search: val,
        sourceOnly: this.sourceOnly,
      })
    );
  };

  sourceFilter = (val: string | AutocompleteOption) => {
    if (typeof val === 'object' && val !== null && 'id' in val) {
      const sourceName = val.isSubsource ? val.subSourceName : val.sourceName;
      const { id } = val;
      const type = typeof val.type === 'string' ? val.type : '';
      return type ? `${sourceName} - ${id} - ${type}` : `${sourceName} - ${id}`;
    }

    if (typeof val === 'string' && val.trim() !== '') {
      return val;
    }

    return '';
  };

  getOptionHtml(val: string | AutocompleteOption): string {
    if (typeof val === 'object' && val !== null) {
      const type = typeof val.type === 'string' ? val.type : '';
      return `
      <div class="flex items-center gap-3">
        <span class="font-bold">${val.isSubsource ? val.subSourceName : val.sourceName}</span>
        <span class="text-sm text-gray-500">(${val.id})</span>
        ${type ? `<span class="text-sm text-white bg-gray-300 px-2 rounded ml-auto">${type}</span>` : ''}
    </div>`;
    }
    return val;
  }

  async sourceChange(source: any) {
    this.sourceSelected.emit(source);

    if (!this.showChips || !source) {
      return;
    }

    const chipSource = {
      ...source,
      descriptor_name: 'source.Source',
    };

    const sourcesChipsValue = [
      ...(this.displayChipsControl.value ?? []),
      chipSource,
    ];
    this.displayChipsControl.setValue(sourcesChipsValue);
    this.sourcesChipsControl.markAsDirty();

    this.control.markAsPristine();
    this.control.markAsUntouched();
  }

  customFormatFunction(value: any): string {
    if (typeof value === 'object' && value !== null) {
      const displayName: string = value.isSubsource
        ? value.subSourceName
        : (value.sourceName ?? value.subSourceName);
      const displayId: string = value.sourceCoverName ?? value.id;
      return displayName && displayName.trim() !== ''
        ? `${displayName} (${displayId})`
        : `${displayId}`;
    }
    return String(value);
  }

  async getSource(id: string, loadingActive = true): Promise<any> {
    if (id) {
      loadingActive && (this.loadingSources = true);
      try {
        const source = await lastValueFrom(
          from(
            this.phoenixTrpc.sources.autocomplete.query({
              search: id,
              sourceOnly: this.sourceOnly,
            })
          )
        );
        loadingActive && (this.source = source?.[0] ?? null);

        this.loadingSources = false;
        return source?.[0] ?? null;
      } catch (error) {
        this.source = null;
        this.loadingSources = false;
        this.uiHelper.handleErrorMessage(error, 'Error loading source!');
      }
    }
  }

  get sourcesChipsLength() {
    return this.sourcesChipsControl.value?.length ?? 0;
  }

  ngOnDestroy(): void {
    this.controlDestroy$.next();
    this.controlDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
